import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { selectSession, SessionActions } from '@state/session';
import { catchError, exhaustMap, filter, map, take } from 'rxjs/operators';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ACCOUNT_TYPE, CARGOCHIEF_KEYS, PAGE_SIZE } from '@domain/constants/app-variables';
import {
	ADD_DAYS,
	DATE_YMD,
	IS_LESS_THAN_CURRENT_TIMESTAMP,
	IS_VALID_DATE,
	RestService,
} from 'cargo-chief-commons';
import { environment } from '@environment/environment';
import { EMPTY, forkJoin, from, of } from 'rxjs';
import { UrlService } from '@service/url/url.service';
import { BaConfigService } from '@service/baConfig/ba-config.service';

@Injectable({
	providedIn: 'root',
})
export class AuthGuardLegacyEffects {
	private static readonly invalidCodes = [401, 422];
	private static readonly invalidKeywords = ['unauthorized', 'session'];
	private lastUrl: string | null = null;

	constructor(
		private store: Store,
		private actions$: Actions<any>,
		private cargoChief: Cargochief,
		private restService: RestService,
		private urlService: UrlService,
		private baConfigService: BaConfigService,
		private router: Router
	) {}

	private static parseError(err) {
		if (
			AuthGuardLegacyEffects.invalidCodes.includes(err?.status) &&
			AuthGuardLegacyEffects.invalidKeywords.some((keyword) =>
				err?.error?.toLowerCase()?.includes(keyword)
			)
		) {
			return of(SessionActions.validate.fail({ payload: { route: null } }));
		}

		return of(EMPTY);
	}

	isRouteChanged(currentUrl: string): boolean {
		if (this.lastUrl !== currentUrl) {
			this.lastUrl = currentUrl;
			return true;
		}
		return false;
	}

	routerNavigated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ROUTER_NAVIGATED),
			exhaustMap(() => this.store.select(selectSession).pipe(take(1))),
			filter((session) => {
				const currentUrl = this.router.url.split('?')[0];
				return session?.loggedIn === true && this.isRouteChanged(currentUrl);
			}),
			exhaustMap((session) => {
				const accountType = session.data.account.accountType?.toUpperCase();
				const isAdmin = accountType === ACCOUNT_TYPE.ADMIN;
				const isBrokerOrShipper =
					accountType === ACCOUNT_TYPE.BROKER || accountType === ACCOUNT_TYPE.SHIPPER;

				// called for all account types
				const accountUsersApi = this.restService
					.GET(
						this.urlService.getAllAccountUsers(
							session.data.account.id,
							0,
							PAGE_SIZE.USERS_LIST_CACHE
						)
					)
					.pipe(
						map((response) => response.data),
						catchError(AuthGuardLegacyEffects.parseError)
					);

				// called for all except admin
				const notificationSettingsApi = isAdmin
					? of(null)
					: this.restService.GET(this.urlService.notificationSettings()).pipe(
							map((response) => response.data),
							catchError(AuthGuardLegacyEffects.parseError)
						);

				// APIs called only for broker/shipper
				const brokerShipperApis = isBrokerOrShipper
					? [
							this.restService.GET(this.urlService.userCarrierMap()).pipe(
								map((response) => response.data),
								catchError(AuthGuardLegacyEffects.parseError)
							),
							this.restService.GET(this.urlService.integrationTypesUrl()).pipe(
								map((response) => response.data),
								catchError(AuthGuardLegacyEffects.parseError)
							),
							from(this.baConfigService.setBaConfig()).pipe(
								catchError(AuthGuardLegacyEffects.parseError)
							),
						]
					: [of(null), of(null), of(null)];

				return forkJoin([accountUsersApi, notificationSettingsApi, ...brokerShipperApis]).pipe(
					map((data) => ({ session, data, isAdmin, isBrokerOrShipper }))
				);
			}),
			map(({ session, data, isAdmin, isBrokerOrShipper }) => {
				const [allAccountUsers, notificationSettings, userCarrierMap, integrationTypes] = data;

				// Check for invalid response in account users (always called)
				if (allAccountUsers?.type === SessionActions.validate.fail.type) {
					return allAccountUsers;
				}

				// Always set account users cache
				this.cargoChief.setItem({ [CARGOCHIEF_KEYS.ACCOUNT_USERS]: allAccountUsers });

				// Set notification settings cache (not for admin)
				if (!isAdmin) {
					if (notificationSettings?.type === SessionActions.validate.fail.type) {
						return notificationSettings;
					}
					this.cargoChief.setItem({
						[CARGOCHIEF_KEYS.NOTIFICATION_SETTINGS]: notificationSettings,
					});
				}

				// Set broker/shipper specific cache items
				if (isBrokerOrShipper) {
					const brokerShipperInvalidResponse = [userCarrierMap, integrationTypes].find(
						(request) => request?.type === SessionActions.validate.fail.type
					);

					if (brokerShipperInvalidResponse) {
						return brokerShipperInvalidResponse;
					}

					this.cargoChief.setItem({ [CARGOCHIEF_KEYS.USER_CARRIER_MAPS]: userCarrierMap });
					this.cargoChief.setItem({
						[CARGOCHIEF_KEYS.INTEGRATION_TYPES]: integrationTypes,
					});
				}

				this.cargoChief.setItem({
					[CARGOCHIEF_KEYS.PAYMENT_AMOUNT]: session.data.account.planAmount,
				});
				this.cargoChief.setItem({
					[CARGOCHIEF_KEYS.NEXT_PAYMENT_DATE_EXPIRED]:
						!session.data.account.nextPaymentDate ||
						(IS_VALID_DATE(DATE_YMD(session.data.account.nextPaymentDate), ['Y-M-D']) &&
							IS_LESS_THAN_CURRENT_TIMESTAMP(
								DATE_YMD(
									ADD_DAYS(session.data.account.nextPaymentDate, environment.PAYMENT_TRANSIT_DAYS)
								)
							)),
				});

				return SessionActions.cache.update();
			})
		)
	);
}
